import DOMPurify from "dompurify";

/**
 * Sanitise manual HTML before rendering. Defence in depth: the server renders
 * doc_json through a schema-constrained TipTap generateHTML (it cannot emit
 * script), and this pass catches anything that slips in via a hostile API
 * response. External links open in a new tab with rel hardening; internal
 * (same-origin) links stay in-app.
 */
const ALLOWED_TAGS = [
  "h2",
  "h3",
  "p",
  "br",
  "strong",
  "em",
  "s",
  "u",
  "a",
  "ul",
  "ol",
  "li",
  "blockquote",
  "code",
  "pre",
  "hr",
  "table",
  "tbody",
  "thead",
  "tr",
  "th",
  "td",
  "colgroup",
  "col",
  "img",
];

function hardenExternalLink(a: Element) {
  const href = a.getAttribute("href") ?? "";
  const external = /^https?:\/\//i.test(href) && !href.startsWith(window.location.origin);
  if (external) {
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noopener noreferrer");
  }
}

DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") hardenExternalLink(node);
});

/** A plain web address in body text; the characters stop at whitespace and markup quotes. */
const URL_RE = /\b(?:https?:\/\/|www\.)[^\s<>"']+/gi;
/** Sentence punctuation after an address belongs to the sentence, not the link. */
const TRAILING_RE = /[.,)\];:]+$/;

/**
 * Turn plain-text web addresses (http://, https://, www.) into links, so an
 * editor who types "www.legislation.nsw.gov.au" gets a live link. Works on
 * the already-sanitised DOM and only builds text and <a> nodes, so no text
 * is ever parsed as markup. Text inside an existing link, <code> or <pre> is
 * left alone.
 */
function linkPlainAddresses(root: DocumentFragment) {
  const doc = root.ownerDocument;
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const texts: Text[] = [];
  for (let n = walker.nextNode(); n; n = walker.nextNode()) {
    const text = n as Text;
    if (!text.parentElement?.closest("a, code, pre")) texts.push(text);
  }
  for (const text of texts) {
    const value = text.data;
    const parts: Node[] = [];
    let last = 0;
    for (const match of value.matchAll(URL_RE)) {
      const address = match[0].replace(TRAILING_RE, "");
      if (!/^(?:https?:\/\/|www\.)[^./]/i.test(address)) continue;
      const start = match.index;
      if (start > last) parts.push(doc.createTextNode(value.slice(last, start)));
      const a = doc.createElement("a");
      a.setAttribute("href", /^www\./i.test(address) ? `https://${address}` : address);
      a.textContent = address;
      hardenExternalLink(a);
      parts.push(a);
      last = start + address.length;
    }
    if (parts.length === 0) continue;
    if (last < value.length) parts.push(doc.createTextNode(value.slice(last)));
    text.replaceWith(...parts);
  }
}

export function sanitizeManualHtml(html: string): string {
  const clean = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR: ["href", "src", "alt", "title", "colspan", "rowspan", "colwidth"],
    ALLOW_DATA_ATTR: false,
  });
  // A <template> parses into an inert document: nothing in it loads or runs.
  const template = document.createElement("template");
  template.innerHTML = clean;
  linkPlainAddresses(template.content);
  return template.innerHTML;
}

/** Search snippets: the server's ts_headline wraps matched terms in <mark>; nothing else is allowed through. */
export function sanitizeSnippet(html: string): string {
  return DOMPurify.sanitize(html, { ALLOWED_TAGS: ["mark"], ALLOWED_ATTR: [] });
}
